import { useState, useEffect, useRef, useCallback } from "react";
import { Check, ChevronsUpDown, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface AsyncSearchableSelectProps<T extends Record<string, any>> {
  fetchItems: (searchTerm: string) => Promise<T[]>;
  displayField: keyof T | ((item: T) => string);
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  loadingMessage?: string;
  value?: T | null;
  onSelect: (item: T | null) => void;
  className?: string;
  disabled?: boolean;
  getItemId?: (item: T) => string | number;
  minSearchLength?: number;
  debounceMs?: number;
}

export function AsyncSearchableSelect<T extends Record<string, any>>({
  fetchItems,
  displayField,
  placeholder = "Selecione uma opção...",
  searchPlaceholder = "Buscar...",
  emptyMessage = "Nenhum item encontrado.",
  loadingMessage = "Buscando...",
  value,
  onSelect,
  className,
  disabled = false,
  getItemId = (item) => item.id || item.uuid,
  minSearchLength = 2,
  debounceMs = 300,
}: AsyncSearchableSelectProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const getDisplayValue = useCallback(
    (item: T) => {
      if (typeof displayField === "function") {
        return displayField(item);
      }
      return String(item[displayField]);
    },
    [displayField]
  );

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (searchTerm.length < minSearchLength) {
      setItems([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const results = await fetchItems(searchTerm);
        setItems(results);
      } catch (error) {
        console.error("Error fetching items:", error);
        setItems([]);
      } finally {
        setLoading(false);
      }
    }, debounceMs);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchTerm, fetchItems, minSearchLength, debounceMs]);

  const handleSelect = (item: T) => {
    onSelect(item);
    setIsOpen(false);
    setSearchTerm("");
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setSearchTerm("");
      setItems([]);
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={isOpen}
          className={cn("w-full justify-between font-normal", className)}
          disabled={disabled}
        >
          {value ? getDisplayValue(value) : placeholder}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={searchPlaceholder}
            className="h-9"
            value={searchTerm}
            onValueChange={setSearchTerm}
          />
          <CommandList>
            {loading ? (
              <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {loadingMessage}
              </div>
            ) : searchTerm.length < minSearchLength ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                Digite pelo menos {minSearchLength} caracteres para buscar
              </div>
            ) : items.length === 0 ? (
              <CommandEmpty>{emptyMessage}</CommandEmpty>
            ) : (
              <CommandGroup>
                {items.map((item) => {
                  const itemId = getItemId(item);
                  const isSelected = value ? getItemId(value) === itemId : false;

                  return (
                    <CommandItem
                      key={String(itemId)}
                      value={String(itemId)}
                      onSelect={() => handleSelect(item)}
                    >
                      {getDisplayValue(item)}
                      <Check
                        className={cn(
                          "ml-auto h-4 w-4",
                          isSelected ? "opacity-100" : "opacity-0"
                        )}
                      />
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export type { AsyncSearchableSelectProps };
